import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiX } = FiIcons;

const Gallery = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const images = [
    {
      url: 'https://i.postimg.cc/C1PWft67/2026-01-23-08-41-17.jpg',
      title: 'Professional CCTV Drain Survey',
      description: 'Advanced camera inspection equipment for accurate diagnostics'
    },
    {
      url: 'https://i.postimg.cc/mDX0Fq68/2026-01-23-08-41-19.jpg',
      title: 'High-Pressure Water Jetting',
      description: 'Powerful jetting equipment for thorough drain cleaning'
    },
    {
      url: 'https://i.postimg.cc/d1HzTXSH/2026-01-23-08-41-22.jpg',
      title: 'Drain Unblocking Service',
      description: 'Professional drain clearing with specialized tools'
    },
    {
      url: 'https://i.postimg.cc/sX6tZ800/2026-01-23-08-41-24.jpg',
      title: 'Emergency Drainage Work',
      description: '24/7 emergency response for urgent drainage issues'
    },
    {
      url: 'https://i.postimg.cc/DZjtbDYx/2026-01-23-08-41-27.jpg',
      title: 'Manhole Inspection',
      description: 'Thorough inspection and cleaning of manholes'
    },
    {
      url: 'https://i.postimg.cc/XJHTyhDP/2026-01-23-08-41-29.jpg',
      title: 'Pipe Repair Service',
      description: 'Professional pipe repair and replacement work'
    },
    {
      url: 'https://i.postimg.cc/d1HzTXSx/2026-01-23-08-41-31.jpg',
      title: 'Commercial Drainage',
      description: 'Drainage solutions for commercial properties'
    },
    {
      url: 'https://i.postimg.cc/RFsjn8by/2026-01-23-08-41-35.jpg',
      title: 'Clean Professional Work',
      description: 'Maintaining cleanliness throughout the service process'
    }
  ];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const openModal = (index) => {
    setCurrentImage(index);
    setIsModalOpen(true);
  };

  return (
    <section id="gallery" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our Work in Action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See our professional drainage engineers at work. From emergency unblocking 
            to CCTV surveys, we bring the right tools and expertise to every job.
          </p>
        </motion.div>

        {/* Image Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group cursor-pointer overflow-hidden rounded-xl shadow-lg"
              onClick={() => openModal(index)}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="text-white text-center p-4">
                  <h3 className="font-semibold mb-2">{image.title}</h3>
                  <p className="text-sm text-gray-200">{image.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="relative h-96 lg:h-[500px]">
            <img
              src={images[currentImage].url}
              alt={images[currentImage].title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="text-xl" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="text-xl" />
            </button>

            {/* Image Info */}
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="text-2xl font-bold mb-2">{images[currentImage].title}</h3>
              <p className="text-gray-200">{images[currentImage].description}</p>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 py-6">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentImage ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-4xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 z-10 bg-white/20 backdrop-blur-sm p-2 rounded-full text-white hover:bg-white/30 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
              <img
                src={images[currentImage].url}
                alt={images[currentImage].title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;